import { CanvasInputMode } from '@service/canvas/canvas-input-mode.service';

type PropertyValueUnion<T extends object> = T[keyof T];

export const canvasEditorOptions = {
    text: CanvasInputMode.TEXT,
    connect: CanvasInputMode.CONNECTOR,
} as const;

export const drawOptions = {
    vector: CanvasInputMode.PATH,
    freeDraw: CanvasInputMode.FREE_DRAW,
} as const;

export const shapes = {
    rectangle: CanvasInputMode.RECTANGLE,
    ellipse: CanvasInputMode.ELLIPSE,
    polygon: CanvasInputMode.POLYGON,
    line: CanvasInputMode.LINE,
    arrow: CanvasInputMode.ARROW,
    addImage: CanvasInputMode.IMAGE,
    addSvg: CanvasInputMode.ADD_SVG,
    triangle: CanvasInputMode.TRIANGLE,
} as const;

export const controls = {
    inputValue: 'inputValue',
    outputValue: 'outputValue',
    bar: 'bar',
    button: CanvasInputMode.BUTTON,
    status: 'status',
    toggleSwitch: 'toggleSwitch',
} as const;

export const extTypes = {
    gauge: CanvasInputMode.GAUGE,
    valve: CanvasInputMode.DEFAULT_VALVE,
    pump: CanvasInputMode.DEFAULT_PUMP,
    fan: CanvasInputMode.DEFAULT_FAN,
    tank: CanvasInputMode.DEFAULT_TANK,
    switch: CanvasInputMode.DEFAULT_SWITCH,
};

export const connectors = {
    straight: CanvasInputMode.CONNECTOR_STRAIGHT,
    corner: CanvasInputMode.CONNECTOR_CORNER,
    direction: CanvasInputMode.CONNECTOR_DIRECTION,
    endpoint: CanvasInputMode.CONNECTOR_ENDPOINT,
    junction: CanvasInputMode.CONNECTOR_JUNCTION,
} as const;

export const functionLables = {
    move: CanvasInputMode.SELECT,
    grab: CanvasInputMode.MOVE_CANVAS,
    group: 'groupSelected',
    ungroup: 'ungroupSelected',
    duplicate: 'duplicate',
};

export type EditorDrawOptions = PropertyValueUnion<typeof drawOptions>;
export type EditorShapeOptions = PropertyValueUnion<typeof shapes>;
export type EditorControlOptions = PropertyValueUnion<typeof controls>;
export type EditorOtherOptions = PropertyValueUnion<typeof canvasEditorOptions>;
export type EditorFunction = PropertyValueUnion<typeof functionLables>;

export type EditorOptions = EditorDrawOptions | EditorShapeOptions | EditorControlOptions | EditorOtherOptions;

export type EditorEntityItem = {
    name: EditorOptions | EditorFunction;
    svgIcon: string;
    layerName?: string;
    tooltip?: string;
};

export const leftFooterOption: EditorEntityItem[] = [
    {
        name: CanvasInputMode.TEXT,
        svgIcon: 'text',
        layerName: 'Text',
    },
    {
        name: CanvasInputMode.CONNECTOR,
        svgIcon: 'connect',
    },
];

export const drawItemList: EditorEntityItem[] = [
    {
        name: drawOptions.freeDraw,
        svgIcon: 'pan',
        layerName: 'Free draw',
    },
    {
        name: drawOptions.vector,
        svgIcon: 'draw-pan',
        layerName: 'Path',
    },
];

export const connectorItemList: EditorEntityItem[] = [
    {
        name: connectors.straight,
        svgIcon: 'line-icon',
    },
    {
        name: connectors.corner,
        svgIcon: 'corner-icon',
    },
    {
        name: connectors.direction,
        svgIcon: 'direction-icon',
    },
    {
        name: connectors.endpoint,
        svgIcon: 'end-point-icon',
    },
    {
        name: connectors.junction,
        svgIcon: 'cross-icon',
    },
];

export const controlItemList: EditorEntityItem[] = [
    {
        name: controls.inputValue,
        svgIcon: 'input-value',
        layerName: 'Value Display',
    },
    {
        name: controls.outputValue,
        svgIcon: 'output-value',
        layerName: 'Output value',
    },
    {
        name: controls.bar,
        svgIcon: 'bar',
        layerName: 'Bar',
    },
    {
        name: controls.button,
        svgIcon: 'button',
        layerName: 'Button',
    },
    {
        name: controls.status,
        svgIcon: 'status',
        layerName: 'Status',
    },
    {
        name: controls.toggleSwitch,
        svgIcon: 'toggle-switch',
        layerName: 'Toggle',
    },
];

export const shapeItemList: EditorEntityItem[] = [
    {
        name: shapes.rectangle,
        svgIcon: 'rectangle',
        layerName: 'Rect',
    },
    {
        name: shapes.ellipse,
        svgIcon: 'ellipse',
        layerName: 'Ellipse',
    },
    {
        name: shapes.triangle,
        svgIcon: 'triangle',
        layerName: 'Triangle',
    },
    {
        name: shapes.line,
        svgIcon: 'line',
        layerName: 'Line',
    },
    {
        name: shapes.addImage,
        svgIcon: 'add-image',
        layerName: 'Image',
    },
    {
        name: shapes.addSvg,
        svgIcon: 'add-svg',
        layerName: 'svg',
    },
];

export const editorFunctionOptionList: EditorEntityItem[] = [
    {
        name: functionLables.move,
        svgIcon: 'move',
        tooltip: 'mimic.canvas.mode.function.move-tooltip',
    },
    {
        name: functionLables.grab,
        svgIcon: 'grab',
        tooltip: 'mimic.canvas.mode.function.grab-tooltip',
    },
    {
        name: functionLables.group,
        svgIcon: 'group',
        tooltip: 'mimic.canvas.mode.function.group-tooltip',
    },
    {
        name: functionLables.ungroup,
        svgIcon: 'ungroup',
        tooltip: 'mimic.canvas.mode.function.ungroup-tooltip',
    },
    {
        name: functionLables.duplicate,
        svgIcon: 'duplicate',
        tooltip: 'mimic.canvas.mode.function.duplicate-tooltip',
    },
];

export const otherTypes: EditorEntityItem[] = [
    {
        name: extTypes.gauge,
        svgIcon: 'gauge',
        layerName: 'Gauge',
    },
    {
        name: extTypes.valve,
        svgIcon: 'valve',
        layerName: 'Valve',
    },
    {
        name: extTypes.pump,
        svgIcon: 'control',
        layerName: 'Pump',
    },
    {
        name: extTypes.fan,
        svgIcon: 'fan',
        layerName: 'Fan',
    },
    {
        name: extTypes.tank,
        svgIcon: 'tank',
        layerName: 'Tank',
    },
    {
        name: extTypes.switch,
        svgIcon: 'toggle-switch',
        layerName: 'Toggle',
    },
];

export const alignOption = {
    left: 'Left',
    centerHorizontal: 'Center horizontal',
    right: 'Right',
    top: 'Top',
    centerVertical: 'Center vertical',
    bottom: 'Bottom',
    letterSpacing: 'Letter spacing',
} as const;

export const positonOption = {
    forward: 'forward',
    front: 'front',
    backwards: 'backwards',
    back: 'back',
} as const;

export const objectOrientation = {
    horizontal: 'horizontal',
    vertical: 'vertical',
} as const;

export type AlignOption = PropertyValueUnion<typeof alignOption>;
export type PositionOption = PropertyValueUnion<typeof positonOption>;
export type ObjectOrientation = PropertyValueUnion<typeof objectOrientation>;

export type LayoutTool = {
    name: AlignOption | PositionOption | ObjectOrientation;
    svgIcon: string;
    tooltip?: string;
};

export const alignToolList: LayoutTool[] = [
    {
        name: alignOption.left,
        svgIcon: 'align-left',
        tooltip: 'mimic.canvas.mode.function.align-left-tooltip',
    },
    {
        name: alignOption.centerHorizontal,
        svgIcon: 'align-center-horizontal',
        tooltip: 'mimic.canvas.mode.function.align-center-horizontal-tooltip',
    },
    {
        name: alignOption.right,
        svgIcon: 'align-right',
        tooltip: 'mimic.canvas.mode.function.align-right-tooltip',
    },
    {
        name: alignOption.top,
        svgIcon: 'align-top',
        tooltip: 'mimic.canvas.mode.function.align-top-tooltip',
    },
    {
        name: alignOption.centerVertical,
        svgIcon: 'align-center-vertical',
        tooltip: 'mimic.canvas.mode.function.align-center-vertical-tooltip',
    },
    {
        name: alignOption.bottom,
        svgIcon: 'align-bottom',
        tooltip: 'mimic.canvas.mode.function.align-bottom-tooltip',
    },
];

export const positionToolList: LayoutTool[] = [
    {
        name: positonOption.forward,
        svgIcon: 'forward',
        tooltip: 'mimic.canvas.mode.function.forward-tooltip',
    },
    {
        name: positonOption.front,
        svgIcon: 'front',
        tooltip: 'mimic.canvas.mode.function.front-tooltip',
    },
    {
        name: positonOption.backwards,
        svgIcon: 'backwards',
        tooltip: 'mimic.canvas.mode.function.backwards-tooltip',
    },
    {
        name: positonOption.back,
        svgIcon: 'back',
        tooltip: 'mimic.canvas.mode.function.back-tooltip',
    },
];

export const objectOrientationToolList: LayoutTool[] = [
    {
        name: objectOrientation.horizontal,
        svgIcon: 'horizontal',
        tooltip: 'mimic.canvas.mode.function.horizontal-tooltip',
    },
    {
        name: objectOrientation.vertical,
        svgIcon: 'vertical',
        tooltip: 'mimic.canvas.mode.function.vertical-tooltip',
    },
];

export type StrokeSize = {
    top: number;
    right: number;
    bottom: number;
    left: number;
};

export const strokeSideList = ['left', 'top', 'right', 'bottom'];

export const lineStyleList = {
    'line-solid': undefined,
    'line-dot': '3,3',
    'line-dashed': '10,10',
    'line-dash-dot': '20,5,5,5',
    'line-dash-dot-dot': '20,10,5,5,5,10',
};

export type LineJoinTool = {
    name: string;
    svgIcon: string;
    tooltip: string;
};

export const lineJoinStyleList: LineJoinTool[] = [
    {
        name: 'linejoin-milter',
        svgIcon: 'miter',
        tooltip: 'mimic.canvas.mode.function.miter-tooltip',
    },
    {
        name: 'linejoin-bevel',
        svgIcon: 'bevel',
        tooltip: 'mimic.canvas.mode.function.bevel-tooltip',
    },
    {
        name: 'linejoin-round',
        svgIcon: 'round',
        tooltip: 'mimic.canvas.mode.function.round-tooltip',
    },
];

export const fontFamilyList = ['Inter', 'Roboto-Regular', 'NotoSans'];

export type CSSStyleObject = {
    [key in keyof CSSStyleDeclaration]?: string;
};

export const editorOptionsMap = new Map<string, string>();

[
    ...connectorItemList,
    ...shapeItemList,
    ...controlItemList,
    ...drawItemList,
    ...leftFooterOption,
    ...otherTypes,
].forEach(item => {
    editorOptionsMap.set(item.name, item.svgIcon);
});

export const editorLayerName = new Map<string, string>();
[
    ...connectorItemList,
    ...shapeItemList,
    ...controlItemList,
    ...drawItemList,
    ...leftFooterOption,
    ...otherTypes,
].forEach(item => {
    editorLayerName.set(item.name, item.layerName);
});

export const extensionSvgPath = '../../../assets/icons/';

export const zoomMaxValue = 2000;
export const zoomMinValue = 50;
export const zoomDependentAttributes = ['width', 'height', 'x', 'y'];

export const hotkey = {
    copy: 'Ctrl+c',
    copyInMac: 'Command+c',
    paste: 'Ctrl+v',
    pasteInMac: 'Command+v',
    front: ']',
    back: '[',
    group: 'g',
    ungroup: 'u',
};

const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);

export const contextMenuItemList = [
    {
        name: 'editor.contextmenu.copy',
        hotkey: isMac ? hotkey.copyInMac : hotkey.copy,
    },
    {
        name: 'editor.contextmenu.paste',
        hotkey: isMac ? hotkey.pasteInMac : hotkey.paste,
    },
    {
        name: 'editor.contextmenu.front',
        hotkey: hotkey.front,
    },
    {
        name: 'editor.contextmenu.back',
        hotkey: hotkey.back,
    },
    {
        name: 'editor.contextmenu.group',
        hotkey: hotkey.group,
    },
    {
        name: 'editor.contextmenu.ungroup',
        hotkey: hotkey.ungroup,
    },
];

export function createTwoDecimal(value: string | number) {
    if (typeof value === 'number') {
        return (Math.round(value * 100) / 100).toString().replace(/\.00$/, '');
    } else {
        return (Math.round(parseFloat(value) * 100) / 100).toString().replace(/\.00$/, '');
    }
}

export enum ElementTagName {
    ellipse = 'ellipse',
    line = 'line',
    path = 'path',
    title = 'title',
    g = 'g',
    rect = 'rect',
    foreignObject = 'foreignObject',
    text = 'text',
    image = 'image',
}

export enum ElementAttribute {
    width = 'width',
    height = 'height',
    x = 'x',
    y = 'y',
    cx = 'cx',
    cy = 'cy',
    rx = 'rx',
    ry = 'ry',
    x1 = 'x1',
    y1 = 'y1',
    x2 = 'x2',
    y2 = 'y2',
    d = 'd',
    constraint = 'constrain-state',
    transform = 'transform',
    strokeWidth = 'stroke-width',
    strokeDashArray = 'stroke-dasharray',
    lineJoinStyle = 'stroke-linejoin',
    type = 'type',
    id = 'id',
    opacity = 'opacity',
    fill = 'fill',
    stroke = 'stroke',
    fillOpacity = 'fill-opacity',
    strokeOpacity = 'stroke-opacity',
    opacityPreviewOption = 'opacity-preview-option',
    opacityPreview = 'opacity-preview',
    fontFamily = 'font-family',
    style = 'style',
    fontSize = 'font-size',
    letterSpacing = 'letter-spacing',
    fontWeight = 'font-weight',
    fontStyle = 'font-style',
    componentId = 'component-id',
    elementComponentId = 'element-component-id',
    resizable = 'data-icreate-resizable',
    connectorType = 'data-icreate-connector-type',
    connectorIcon = 'data-icreate-connector-icon',
    pumpIcon = 'data-icreate-pump-icon',
    iCreateType = 'data-icreate-type',
    layerName = 'layer-name',
    barScaleType = 'bar-scale-type',
    barIntervals = 'bar-intervals',
    gaugeDivisions = 'gauge-divisions',
    gaugeCustomDivisions = 'gauge-custom-divisions',
    gaugeScaleValues = 'gauge-scale-values',
    gaugeUnitDisplay = 'gauge-unit-display',
    gaugeValueDisplay = 'gauge-value-display',
    gaugeNameDisplay = 'gauge-name-display',
    gaugeTitle = 'gauge-title',
    gaugeTitlePosition = 'gauge-title-position',
}

export const optionName = {
    createNew: 'mimic.option-dialog.create-new',
    sortFolders: 'mimic.option-dialog.sort-folders',
    import: 'mimic.option-dialog.import',
    export: 'mimic.option-dialog.export',
    print: 'mimic.option-dialog.print',
    folder: 'mimic.option-dialog.folder',
    mimic: 'mimic.option-dialog.mimic',
    convertPDF: 'mimic.option-dialog.convert-pdf',
    nameAsc: 'mimic.option-dialog.name-asc',
    nameDesc: 'mimic.option-dialog.name-desc',
    moveMimic: 'mimic.option-dialog.move-mimic',
    duplicate: 'mimic.option-dialog.duplicate',
    delete: 'mimic.option-dialog.delete',
    moveToFolder: 'mimic.option-dialog.move-to-folder',
    moveUp: 'mimic.option-dialog.move-up',
    moveDown: 'mimic.option-dialog.move-down',
    rename: 'mimic.option-dialog.rename',
    createMimic: 'mimic.option-dialog.create-mimic',
    outOfFolder: 'mimic.option-dialog.out-of-folder',
};

export type OptionItem = {
    label: string;
    svg?: string;
    arrowIcon?: boolean;
    subOptionItem?: OptionItem;
    new?: boolean;
    disabled?: boolean;
    folder: any;
};

export const fileOptioinItemList = [
    {
        label: optionName.createNew,
        svg: 'plus',
        arrowIcon: true,
        subOptionItem: [
            { label: optionName.folder, arrowIcon: false, new: true },
            { label: optionName.mimic, arrowIcon: false, new: true },
            { label: optionName.convertPDF, svg: 'convert-pdf', arrowIcon: false },
        ],
    },
    {
        label: optionName.sortFolders,
        svg: 'sort-asc',
        arrowIcon: true,
        subOptionItem: [
            { label: optionName.nameAsc, svg: 'sort-asc', arrowIcon: false },
            { label: optionName.nameDesc, svg: 'sort-desc', arrowIcon: false },
        ],
    },
    { label: optionName.import, svg: 'import', arrowIcon: false },
    { label: optionName.export, svg: 'export', arrowIcon: false },
    { label: optionName.print, svg: 'print', arrowIcon: false },
];

export const mimicOptioinItemList = [
    {
        label: optionName.moveMimic,
        arrowIcon: true,
        subOptionItem: [
            {
                label: optionName.moveToFolder,
                arrowIcon: true,
                new: false,
                subOptionItem: [],
            },
            { label: optionName.moveUp, arrowIcon: false, new: false },
            { label: optionName.moveDown, arrowIcon: false, new: false },
        ],
    },
    {
        label: optionName.duplicate,
        svg: 'duplicate',
        arrowIcon: false,
    },
    { label: optionName.delete, svg: 'delete', arrowIcon: false },
    { label: optionName.import, svg: 'import', arrowIcon: false },
    { label: optionName.export, svg: 'export', arrowIcon: false },
    { label: optionName.print, svg: 'print', arrowIcon: false },
];

export const folderOptioinItemList = [
    { label: optionName.createMimic, arrowIcon: false, new: true },
    { label: optionName.moveUp, arrowIcon: false, new: false, disabled: null },
    { label: optionName.moveDown, arrowIcon: false, new: false, disabled: null },
    { label: optionName.rename, arrowIcon: false, new: false },
    { label: optionName.delete, arrowIcon: false },
    {
        label: optionName.duplicate,
        arrowIcon: false,
    },
];
